// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useNavigate, useSearchParams } from "react-router-dom"
import { useForges } from "@/queries/queries.forges"
import SmartTable from "@/components/ui/SmartTable"
import { useTableUrlState } from "@/hooks/useTableUrlState"
import { useUrlParamWriter } from "@/hooks/useUrlParamWriter"
import {
  forgeColumns,
  FORGE_DEFAULT_HIDDEN,
  FORGE_NON_HIDEABLE,
} from "./columns"
import { ColumnsMenu, ForgesSearchField } from "./toolbar"
import {
  ForgesError,
  ForgesOnboarding,
  ForgesSkeleton,
  NoForgesMatch,
} from "./states"

/**
 * "My Forges" (`/forges`) — the owner's forges, one row per forge,
 * answering activity (last-cycle status + date), quality (the forge's
 * Arena record) and spend (cumulative cost) at a glance.
 *
 * The rows arrive already sorted by the backend (`last_cycle_at DESC`
 * NULLS LAST, tiebreak `created_at DESC`) and are rendered verbatim;
 * sorting, searching and pagination are computed client-side in v1
 * (server-side search/sort/pagination is the deferred cursor issue).
 *
 * The URL is the only home of the view state (ADR-0006): the name filter
 * (`?q=`), the table sort (`?sort=<key>:<asc|desc>`) and the current page
 * (`?page=<n>`, via `useTableUrlState`). A view is
 * therefore shareable, survives a reload and round-trips through
 * back/forward. The defaults — no filter, no sort, page 1 — are absent
 * from the URL, so the canonical link stays clean. A new filter or a new
 * ordering renumbers the pages, so both reset `page`.
 */
const FORGE_COLUMN_IDS = forgeColumns.map((column) => column.id as string)

const ForgesListView = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const write = useUrlParamWriter()
  const { data, isLoading, isError, refetch } = useForges()

  const query = searchParams.get("q") ?? ""

  // Sort, page and column visibility live in the URL (ADR-0006) via the
  // codec binding; the toolbar's Columns popover (outside SmartTable) and
  // the table share the one visibility map it derives.
  const {
    sorting,
    onSortingChange,
    pageIndex,
    onPageIndexChange,
    columnVisibility,
    onColumnVisibilityChange,
  } = useTableUrlState({
    columnIds: FORGE_COLUMN_IDS,
    defaultHidden: FORGE_DEFAULT_HIDDEN,
    nonHideable: FORGE_NON_HIDEABLE,
  })

  // A new filter renumbers the pages, so the query write resets `page`.
  const setQuery = (value: string) =>
    write({
      q: value.trim().length === 0 ? null : value,
      page: null,
    })

  const items = data?.items ?? []
  const needle = query.trim().toLowerCase()
  const matches = needle
    ? items.filter((forge) => forge.name.toLowerCase().includes(needle))
    : items

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-[18px] pb-16">
      <div className="flex flex-row items-center gap-3">
        <h1 className="text-[25px] font-semibold tracking-[-0.015em] text-foreground">
          My Forges
        </h1>
        {data && (
          <span className="font-mono text-xs text-text-tertiary">
            {matches.length} forge{matches.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {isLoading && <ForgesSkeleton />}
      {/* The error state is for having nothing to show: with rows on
          screen, a failed refetch must never stack an alert above them. */}
      {isError && !data && <ForgesError onRetry={() => refetch()} />}

      {data && items.length === 0 && <ForgesOnboarding />}

      {data && items.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <ForgesSearchField value={query} onChange={setQuery} />
            <span className="flex-1" />
            <ColumnsMenu
              columns={forgeColumns}
              visibleColumns={columnVisibility}
              onToggle={(key) =>
                onColumnVisibilityChange({
                  ...columnVisibility,
                  [key]: !columnVisibility[key],
                })
              }
            />
          </div>
          {matches.length === 0 ? (
            <NoForgesMatch query={query} onClear={() => setQuery("")} />
          ) : (
            <SmartTable
              data={matches}
              columns={forgeColumns}
              pageSize={20}
              sorting={sorting}
              onSortingChange={onSortingChange}
              pageIndex={pageIndex}
              onPageIndexChange={onPageIndexChange}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={onColumnVisibilityChange}
              onRowClick={(forge: Forge) =>
                navigate(`/forges/${forge.forge_id}`)
              }
            />
          )}
        </>
      )}
    </div>
  )
}

export default ForgesListView
